// packages/shared/src/catalog/enums.ts
//
// Catalog domain string enum registry + error code constants.
// Cross-module source of truth — imported by services (Wave 6) and routes (Wave 7).
//
// Decisions implemented:
//   - CATALOG_* error codes (19 canonical + 4 additional = 23 total)
//   - RBAC permission keys (13 explicit write/approve + catalog:read scope)
//   - Deal lifecycle + dealScope enum
//   - Quyết định #47 (PrintMethod 9-canonical per ARTWORK-TEMPLATE-SPEC §2.0 v3.2)
//
// Export pattern: `as const` tuple → derived union type → Zod schema.
// Node-pure (no Fastify / Mongoose imports). Safe to bundle in Vite client builds.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// BlueprintStatus — Draft → Active → Inactive state machine
// Per SPECS.md §4 Blueprint Lifecycle + AC-03
// ---------------------------------------------------------------------------
// Phase 8.2.1 Amendment #3: PendingApproval state added for admin self-create
// maker-checker flow. Blueprint stays PendingApproval until approval row (in
// product_approvals collection) is approved by different admin (4-eyes).
export const BLUEPRINT_STATUSES = ['Draft', 'PendingApproval', 'Active', 'Inactive'];
export const BlueprintStatusSchema = z.enum(BLUEPRINT_STATUSES);
// Phase 8.2.1 Amendment #3: product_approvals.type enum extended with
// admin_self_create — admin POST /admin/blueprints triggers row insert with
// actorType='admin', goes through same unified approve/reject queue as vendor.
export const PRODUCT_APPROVAL_TYPES = [
    'new_submission',
    're_approval',
    'admin_self_create',
];
export const ProductApprovalTypeSchema = z.enum(PRODUCT_APPROVAL_TYPES);
// Phase 8.2.1 Amendment #4 A1 (Codex r2): actorType discriminator on
// product_approvals row. 'vendor' = legacy vendor submission flow (uses
// vendorId). 'admin' = admin self-create (uses actorUserId).
export const PRODUCT_APPROVAL_ACTOR_TYPES = ['vendor', 'admin'];
export const ProductApprovalActorTypeSchema = z.enum(PRODUCT_APPROVAL_ACTOR_TYPES);
// ---------------------------------------------------------------------------
// VariantStockStatus — per SPECS.md §6 Variants + AC-05
// ---------------------------------------------------------------------------
export const VARIANT_STOCK_STATUSES = [
    'InStock',
    'OutOfStock',
    'Discontinued',
];
export const VariantStockStatusSchema = z.enum(VARIANT_STOCK_STATUSES);
// ---------------------------------------------------------------------------
// MerchantProductStatus — per SPECS.md §13 + AC-19 + Quyết định #23
// Unpublished → Published → UnpublishChanges state machine
// ---------------------------------------------------------------------------
export const MERCHANT_PRODUCT_STATUSES = [
    'Unpublished',
    'Published',
    'UnpublishChanges',
];
export const MerchantProductStatusSchema = z.enum(MERCHANT_PRODUCT_STATUSES);
// ---------------------------------------------------------------------------
// DealStatus — per SPECS.md §14 + AC-31 (Deal lifecycle)
// Draft → Pending → Active → Paused → Expired / Rejected
// ---------------------------------------------------------------------------
export const DEAL_STATUSES = [
    'Draft',
    'Pending',
    'Active',
    'Paused',
    'Expired',
    'Rejected',
];
export const DealStatusSchema = z.enum(DEAL_STATUSES);
// ---------------------------------------------------------------------------
// DealType — per SPECS.md §14 + CRUD-SURFACES.md deals.business_flow
// Loại A (volume/revenue): volume_revenue
// Loại B (direct price): direct_price
// ---------------------------------------------------------------------------
export const DEAL_TYPES = ['volume_revenue', 'direct_price'];
export const DealTypeSchema = z.enum(DEAL_TYPES);
// ---------------------------------------------------------------------------
// DealScope — (Deal Scope + Add-on Override Resolution Order)
// Applied as discriminator in pricing pipeline step 4
// ---------------------------------------------------------------------------
export const DEAL_SCOPES = [
    'variant_only',
    'variant_plus_addons',
    'whole_order',
];
export const DealScopeSchema = z.enum(DEAL_SCOPES);
// ---------------------------------------------------------------------------
// AddonChoiceMode — per SPECS.md §8 Add-ons + AC-07
// ---------------------------------------------------------------------------
export const ADDON_CHOICE_MODES = ['Single', 'Multi'];
export const AddonChoiceModeSchema = z.enum(ADDON_CHOICE_MODES);
// ---------------------------------------------------------------------------
// AddonStatus — per SPECS.md §8 Add-ons + AC-07
// Active/Inactive (inactive → ẩn khỏi blueprints đang dùng)
// ---------------------------------------------------------------------------
export const ADDON_STATUSES = ['Active', 'Inactive'];
export const AddonStatusSchema = z.enum(ADDON_STATUSES);
// ---------------------------------------------------------------------------
// VendorProductStatus — per SPECS.md §10 Vendor Submit Product + AC-13/AC-14
// catalog_pick → Live; submit_new → Pending → Approved/Rejected
// ---------------------------------------------------------------------------
export const VENDOR_PRODUCT_STATUSES = [
    'Pending',
    'Approved',
    'Rejected',
    'Live',
];
export const VendorProductStatusSchema = z.enum(VENDOR_PRODUCT_STATUSES);
// ---------------------------------------------------------------------------
// PrintMethod — CRITICAL: 9 canonical methods per Quyết định #47
// Source: ARTWORK-TEMPLATE-SPEC.md §2.0 (v3.2 — CrossAI R4 verified)
//
// PRE-R5 SUPERSEDED names (MUST NOT appear in this list):
//   ✗ sublimation      → replaced by sublimation_transfer + sublimation_aop
//   ✗ laser            → replaced by laser_engrave
//   ✗ heat_transfer    → replaced by htv (heat transfer vinyl)
//   ✗ all_over_print   → subsumed by sublimation_aop
// ---------------------------------------------------------------------------
export const PRINT_METHODS = [
    'DTG', // Direct-to-Garment
    'DTF', // Direct-to-Film
    'sublimation_transfer', // sublimation transfer (flat panel)
    'sublimation_aop', // sublimation all-over-print, cut-and-sew panels
    'screen_print', // screen printing
    'embroidery', // embroidery (EmbroideryArea discriminator)
    'uv', // UV printing
    'htv', // heat transfer vinyl
    'laser_engrave', // laser engraving
];
// Sanity assertion: tuple MUST have exactly 9 elements
// (TypeScript const assertion guarantees this at compile time)
const _assertPrintMethodCount = PRINT_METHODS.length;
void _assertPrintMethodCount; // prevent unused-variable lint
export const PrintMethodSchema = z.enum(PRINT_METHODS);
// ---------------------------------------------------------------------------
// AssetRole — per ARTWORK-TEMPLATE-SPEC §2.0
// source: merchant upload → full validation + normalize
// production_ready: pre-separated/pre-digitized → minimal QA only
// ---------------------------------------------------------------------------
export const ASSET_ROLES = ['source', 'production_ready'];
export const AssetRoleSchema = z.enum(ASSET_ROLES);
// ---------------------------------------------------------------------------
// ExtraCostTrigger — per ARTWORK-TEMPLATE-SPEC §2.1 extraCostRules[] + AC-48
// 6 canonical triggers consumed by P2 Pricing module
// ---------------------------------------------------------------------------
export const EXTRA_COST_TRIGGERS = [
    'extra_color', // additional spot colors beyond base (screen_print / htv)
    'extra_location', // additional print location on garment
    'oversize', // garment/artwork dimension exceeds standard limits
    'white_underbase', // white underbase layer required (DTG on dark fabric)
    'screen_setup_fee', // per-screen setup charge (screen_print first run)
    'rush_fee', // expedited production surcharge
];
// Sanity assertion: exactly 6 triggers per AC-48
const _assertExtraCostTriggerCount = EXTRA_COST_TRIGGERS.length;
void _assertExtraCostTriggerCount;
export const ExtraCostTriggerSchema = z.enum(EXTRA_COST_TRIGGERS);
// ---------------------------------------------------------------------------
// VendorApprovalMode — per SPECS.md §9 + AC-12
// Admin config: which vendors require cost-update approval
// ---------------------------------------------------------------------------
export const VENDOR_APPROVAL_MODES = ['ALL', 'WHITELIST', 'BLACKLIST'];
export const VendorApprovalModeSchema = z.enum(VENDOR_APPROVAL_MODES);
// ---------------------------------------------------------------------------
// RbacKey — Permissions & RBAC (Phase 2)
// 12 explicit write/approve keys + catalog:read scope = 13 total
// (PR-4 dropped the vendor-portal key vendor-product:submit)
// ---------------------------------------------------------------------------
export const RBAC_KEYS = [
    // Blueprint management (Admin/SuperAdmin)
    'blueprint:create',
    'blueprint:update',
    'blueprint:publish',
    // Pricing management (Admin/SuperAdmin)
    'pricing:update',
    // Deal management (Admin/SuperAdmin/Sale)
    'deal:create',
    'deal:approve', // Admin/SuperAdmin only; Sale lacks this key
    // Vendor product workflow
    'vendor-product:review', // Admin: approve/reject vendor product
    // Taxonomy management (Admin/SuperAdmin)
    'category:manage',
    'option:manage',
    'collection:manage',
    'accessory:manage',
    'addon:manage',
    // Read scope — Admin: scope=all; Vendor: scope=own
    'catalog:read',
];
// Sanity assertion: 13 keys = 12 explicit write/approve + catalog:read
const _assertRbacKeyCount = RBAC_KEYS.length;
void _assertRbacKeyCount;
export const RbacKeySchema = z.enum(RBAC_KEYS);
// ---------------------------------------------------------------------------
// CatalogErrorCode — Error Codes & Validation
//
// Base list from (19 codes) + 4 additional codes mandated by task:
//   CATALOG_DEAL_SCOPE_INVALID — deal scope validation
//   CATALOG_SKU_COLLISION      — SKU generation collision detected
//   SKU_COLLISION_FAILED — SKU generation max retries exceeded
//   CHECKOUT_SESSION_EXPIRED — checkout session TTL expired (409)
//
// Total: 23 codes (≥18 acceptance criterion satisfied)
// ---------------------------------------------------------------------------
export const CATALOG_ERROR_CODES = [
    // Blueprint
    'CATALOG_BLUEPRINT_NOT_FOUND',
    'CATALOG_BLUEPRINT_INACTIVE',
    // Variant + SKU
    'CATALOG_VARIANT_PRICE_MISSING',
    'CATALOG_VARIANT_DISCONTINUED',
    'CATALOG_SKU_COLLISION',
    // Category
    'CATALOG_CATEGORY_NOT_FOUND',
    'CATALOG_CATEGORY_HAS_BLUEPRINTS',
    // Option
    'CATALOG_OPTION_VALUE_IN_USE',
    'CATALOG_OPTION_NOT_FOUND',
    // Pricing
    'CATALOG_PRICING_REASON_REQUIRED',
    // Deal
    'CATALOG_DEAL_CONFLICT',
    'CATALOG_DEAL_NOT_FOUND',
    'CATALOG_DEAL_EXPIRED',
    'CATALOG_DEAL_SCOPE_INVALID',
    // Collection
    'CATALOG_COLLECTION_NOT_FOUND',
    // Add-on
    'CATALOG_ADDON_NOT_FOUND',
    'CATALOG_ADDON_INACTIVE',
    // Accessory
    'CATALOG_ACCESSORY_NOT_FOUND',
    // Snapshot
    'CATALOG_SNAPSHOT_NOT_FOUND',
    // Vendor product
    'CATALOG_VENDOR_PRODUCT_DUPLICATE',
    'CATALOG_VENDOR_COST_PENDING',
    // Import
    'CATALOG_IMPORT_VALIDATION_FAILED',
    // SKU registry
    'SKU_COLLISION_FAILED',
    'CATALOG_SKU_INVALID_FORMAT',
    'SKU_IMMUTABLE',
    // Checkout session
    'CHECKOUT_SESSION_EXPIRED',
];
const _errorCodeAssert = true;
void _errorCodeAssert;
export const CatalogErrorCodeSchema = z.enum(CATALOG_ERROR_CODES);
//# sourceMappingURL=enums.js.map